// =============================================================================
// BADGE COMPONENT WITH ICONS
// =============================================================================
// Purpose: Status badges that don't rely on color alone (WCAG 1.4.1)
//
// The non-color distinction is the badge's TEXT, which is required — every call
// site renders a label or a value inside it. `icon` is optional reinforcement
// and, measured across the app, only 24 of 581 call sites pass one.
//
// The previous comment here read "All badges include icons", which is not true
// and never was. Left as a caution: a component comment asserting a guarantee is
// not the same as the component enforcing it — Button's header claimed a 44x44
// touch target while setting only min-height (see UI-40).

import FaIcon from './FaIcon';

type BadgeVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'purple'
  | 'orange'
  | 'teal'
  | 'indigo'
  | 'rose'
  | 'sky';

export type { BadgeVariant };

type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: React.ReactNode;
  dot?: boolean; // Show status dot instead of icon
  /** Animate the status dot — for live/critical states. */
  pulse?: boolean;
  className?: string;
}

export default function Badge({
  children,
  variant = 'neutral',
  size = 'md',
  icon,
  dot = false,
  pulse = false,
  className = '',
}: BadgeProps) {
  // Variant styles with strong contrast for readability
  const variantStyles: Record<BadgeVariant, string> = {
    success:
      'bg-green-100 text-green-800 ring-1 ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-500/30',
    warning:
      'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-500/30',
    danger:
      'bg-red-100 text-red-800 ring-1 ring-red-600/20 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-500/30',
    // Informational uses the BRAND ramp, not a second blue.
    //
    // This was `blue-*` (#3b82f6) while the app's accent is `primary-*`
    // (#2181a8, a blue-teal). Two different blues sitting next to each other —
    // an info badge beside a primary button, a "minor" flood category beside a
    // teal control — read as one of them being wrong, because one of them is.
    // The colour budget already says primary is the single accent; info was
    // simply never folded in when purple and orange were.
    info:
      'bg-primary-100 text-primary-800 ring-1 ring-primary-600/20 dark:bg-primary-900/30 dark:text-primary-200 dark:ring-primary-500/30',
    neutral:
      'bg-gray-100 text-gray-800 ring-1 ring-gray-600/20 dark:bg-white/10 dark:text-gray-300 dark:ring-white/15',
    // Color budget: decorative hues are retired. The variant names remain for
    // API compat but resolve to in-budget styles — orange joins the warning
    // (amber) severity family; the rest render as neutral.
    purple:
      'bg-gray-100 text-gray-800 ring-1 ring-gray-600/20 dark:bg-white/10 dark:text-gray-300 dark:ring-white/15',
    orange:
      'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-500/30',
    teal:
      'bg-gray-100 text-gray-800 ring-1 ring-gray-600/20 dark:bg-white/10 dark:text-gray-300 dark:ring-white/15',
    indigo:
      'bg-gray-100 text-gray-800 ring-1 ring-gray-600/20 dark:bg-white/10 dark:text-gray-300 dark:ring-white/15',
    rose:
      'bg-gray-100 text-gray-800 ring-1 ring-gray-600/20 dark:bg-white/10 dark:text-gray-300 dark:ring-white/15',
    sky:
      'bg-gray-100 text-gray-800 ring-1 ring-gray-600/20 dark:bg-white/10 dark:text-gray-300 dark:ring-white/15',
  };

  // Size styles - larger for better readability
  const sizeStyles: Record<BadgeSize, string> = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
    lg: 'px-3 py-1.5 text-base gap-2',
  };

  const iconSizeClass = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <span
      className={`inline-flex items-center font-medium rounded-md ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
    >
      {dot && (
        <span
          className={`${size === 'sm' ? 'h-1.5 w-1.5' : size === 'lg' ? 'h-2.5 w-2.5' : 'h-2 w-2'} rounded-full bg-current ${pulse ? 'animate-pulse' : ''}`}
          aria-hidden="true"
        />
      )}
      {icon && !dot && (
        <span className={iconSizeClass} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}

// =============================================================================
// STATUS BADGE VARIANTS
// =============================================================================
// Pre-configured badge components for common status types

interface StatusBadgeProps {
  status: string;
  size?: BadgeSize;
  className?: string;
}

// Task status badges with icons
export function TaskStatusBadge({ status, size = 'md', className }: StatusBadgeProps) {
  const statusConfig: Record<string, { variant: BadgeVariant; icon: React.ReactNode; label: string }> = {
    open: {
      variant: 'info',
      icon: <FaIcon icon="clock" className="h-3 w-3" />,
      label: 'Open',
    },
    assigned: {
      variant: 'purple',
      icon: <FaIcon icon="user" className="h-3 w-3" />,
      label: 'Assigned',
    },
    in_progress: {
      variant: 'warning',
      icon: <FaIcon icon="play" className="h-3 w-3" />,
      label: 'In Progress',
    },
    pending_review: {
      variant: 'orange',
      icon: <FaIcon icon="eye" className="h-3 w-3" />,
      label: 'Pending Review',
    },
    on_hold: {
      variant: 'neutral',
      icon: <FaIcon icon="pause" className="h-3 w-3" />,
      label: 'On Hold',
    },
    // The Tasks module uses 'complete'; some other modules use 'completed'.
    // Both must map to the same badge or the fallback silently shows "Open".
    complete: {
      variant: 'success',
      icon: <FaIcon icon="circle-check" className="h-3 w-3" />,
      label: 'Complete',
    },
    completed: {
      variant: 'success',
      icon: <FaIcon icon="circle-check" className="h-3 w-3" />,
      label: 'Completed',
    },
    cancelled: {
      variant: 'danger',
      icon: <FaIcon icon="circle-xmark" className="h-3 w-3" />,
      label: 'Cancelled',
    },
  };

  const config = statusConfig[status] || statusConfig.open;

  return (
    <Badge variant={config.variant} size={size} icon={config.icon} className={className}>
      {config.label}
    </Badge>
  );
}

// Incident status badges with icons
export function IncidentStatusBadge({ status, size = 'md', className }: StatusBadgeProps) {
  const statusConfig: Record<string, { variant: BadgeVariant; icon: React.ReactNode; label: string }> = {
    planned: {
      variant: 'neutral',
      icon: <FaIcon icon="calendar" className="h-3 w-3" />,
      label: 'Planned',
    },
    active: {
      variant: 'danger',
      icon: <FaIcon icon="triangle-exclamation" className="h-3 w-3" />,
      label: 'Active',
    },
    stabilizing: {
      variant: 'warning',
      icon: <FaIcon icon="chart-bar" className="h-3 w-3" />,
      label: 'Stabilizing',
    },
    recovery: {
      variant: 'info',
      icon: <FaIcon icon="arrow-rotate-right" className="h-3 w-3" />,
      label: 'Recovery',
    },
    closed: {
      variant: 'success',
      icon: <FaIcon icon="circle-check" className="h-3 w-3" />,
      label: 'Closed',
    },
    archived: {
      variant: 'neutral',
      icon: <FaIcon icon="box-archive" className="h-3 w-3" />,
      label: 'Archived',
    },
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <Badge variant={config.variant} size={size} icon={config.icon} className={className}>
      {config.label}
    </Badge>
  );
}

// Priority badges with icons
export function PriorityBadge({ priority, size = 'md', className }: { priority: string; size?: BadgeSize; className?: string }) {
  const priorityConfig: Record<string, { variant: BadgeVariant; icon: React.ReactNode }> = {
    low: {
      variant: 'info',
      icon: <FaIcon icon="chevron-down" className="h-3 w-3" />,
    },
    medium: {
      variant: 'warning',
      icon: <FaIcon icon="minus" className="h-3 w-3" />,
    },
    high: {
      variant: 'orange',
      icon: <FaIcon icon="chevron-up" className="h-3 w-3" />,
    },
    // TaskPriority's highest tier is 'urgent'; SupplyRequestPriority has both
    // 'urgent' and 'critical'. Both must resolve or the fallback shows "Medium".
    urgent: {
      variant: 'danger',
      icon: <FaIcon icon="triangle-exclamation" className="h-3 w-3" />,
    },
    critical: {
      variant: 'danger',
      icon: <FaIcon icon="triangle-exclamation" className="h-3 w-3" />,
    },
  };

  const config = priorityConfig[priority.toLowerCase()] || priorityConfig.medium;

  return (
    <Badge variant={config.variant} size={size} icon={config.icon} className={className}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </Badge>
  );
}
